import { z } from 'zod'
import { color, fen, uciMove } from './chess.ts'

export const puzzleId = z.string().min(1).max(32)
export type PuzzleId = z.infer<typeof puzzleId>

/**
 * A puzzle exactly as it arrives from the Lichess puzzle database.
 *
 * Lichess convention, which the rest of the codebase inherits: `fen` is the
 * position *before* the opponent's setup move, and `solution[0]` is that setup
 * move. So the human plays the odd indices (1, 3, 5...) and the opponent
 * answers on the even ones. See `presentPuzzle` in @shess/engine.
 */
export const puzzle = z.object({
  id: puzzleId,
  fen,
  solution: z.array(uciMove).min(2),
  rating: z.number().int().min(0).max(4000),
  ratingDeviation: z.number().int().min(0),
  themes: z.array(z.string()),
  popularity: z.number().int(),
  nbPlays: z.number().int().min(0),
  gameUrl: z.string().url().nullable(),
  openingTags: z.array(z.string()),
})

export type Puzzle = z.infer<typeof puzzle>

/**
 * What the client is allowed to see. Notably absent: the solution. Move
 * validation is server-side, so there is nothing in here to cheat with.
 */
export const puzzleView = z.object({
  id: puzzleId,
  /** Position before the setup move — animate `setupMove` from here. */
  fen,
  setupMove: uciMove,
  /** Position the player actually solves from (after `setupMove`). */
  positionFen: fen,
  /** The side the player moves. */
  playerColor: color,
  rating: z.number().int(),
  themes: z.array(z.string()),
  /** How many moves the player must find. */
  playerMoveCount: z.number().int().min(1),
})

export type PuzzleView = z.infer<typeof puzzleView>
